<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useEventsBus from '@/utils/eventbus';
import { useLocaleStore } from '@/stores/locale';
import api from '@/http';
import PageLoader from '@/components/reusables/PageLoader.vue';
import { useReferenceStore } from '@/stores/reference';

const { gOn } = useEventsBus();
const sidebarIsHide = ref(false);

const { locale } = useI18n(); // Access i18n locale
const refStore = useReferenceStore();
const localeStore = useLocaleStore();

const { currentRoute } = useRouter();
const defaultLayout = import.meta.env.VITE_DEFAULT_THEME;

const orientationText = ref('');

const layout = computed(() => `${currentRoute.value.meta.layout || defaultLayout}-layout`);

const updateOrientation = () => {
  const orientation = window.screen.orientation.type;

  if (orientation.includes('portrait-primary')) {
    orientationText.value = 'Portrait (normal)';
  } else if (orientation.includes('portrait-secondary')) {
    orientationText.value = 'Portrait (upside-down)';
  } else if (orientation.includes('landscape-primary')) {
    orientationText.value = 'Landscape (rotated 90 degrees)';
  } else if (orientation.includes('landscape-secondary')) {
    orientationText.value = 'Landscape (rotated -90 degrees)';
  } else {
    orientationText.value = 'Unknown';
  }
};

onMounted(() => {
  // eslint-disable-next-line no-debugger
  updateOrientation();
  window.addEventListener('orientationchange', updateOrientation);
  //cek auth info
  pingBackend();
  refStore.getCompanyList();
});

onUnmounted(() => {
  window.removeEventListener('orientationchange', updateOrientation);
});

const showSidebar = (status: boolean) => {
  if (status === true) {
    document.body.classList.add('sidebar-show');
  } else {
    document.body.classList.remove('sidebar-show');
  }
};

const toggleSidebar = () => {
  sidebarIsHide.value = !sidebarIsHide.value;
  document.body.classList.remove('sidebar-show');
  if (sidebarIsHide.value === true) {
    document.body.classList.add('sidebar-hide');
  } else {
    document.body.classList.remove('sidebar-hide');
  }
};

const toggleMessageContent = (status: boolean) => {
  if (!window.matchMedia('(max-width: 1199px)').matches) return;
  if (status === true) {
    document.body.classList.add('messagecontent-show');
  } else {
    document.body.classList.remove('messagecontent-show');
  }
};

gOn('showSidebar', showSidebar);
gOn('toggleSidebar', toggleSidebar);
gOn('toggleMessageContent', toggleMessageContent);

//watch locale change
watch(
  () => localeStore.currentLocale,
  (newValue) => (locale.value = newValue)
);

const pingBackend = async () => {
  await api.get(`/`);
};


</script>
<template>
  <component :is="layout">
    <router-view v-slot="{ Component }">
      <component :is="Component" :key="currentRoute.name"></component>
    </router-view>
    <message-box />
    <PageLoader/>
  </component>
</template>
<style lang="css" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
